import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DictType, KeyValueItem } from "@/model";
import { formatJSON, isValidJSON } from "@/util/stringUtil";
import { keyValueArrayToDict } from "@/util/typeUtil";
import { SortableItem } from "@/component/SortableWrapper";


type ValueType = "non-object" | "object-string" | "object";

const valueFormatType = (value: string): ValueType => {
    if (typeof value === "object") {
        return "object";
    }
    if (typeof value === "string" && isValidJSON(value)) {
        return "object-string";
    }
    return "non-object";
};

const isObjectLike = (valueType: ValueType): boolean => {
    return valueType === "object" || valueType === "object-string";
};

export const useConstantObjectValueHandler = (sortableConstants: SortableItem<KeyValueItem>[]) => {
    const isFirstChange = useRef(true);
    const [defaultValueType, setDefaultValueType] = useState<DictType<ValueType>>({});

    const getFormatChangedKeys = useCallback((constants: SortableItem<KeyValueItem>[]): string[] => {
        return constants
                .filter(item => isObjectLike(defaultValueType[item.id]) && !isValidJSON(item.data.value))
                .map(item => item.data.key);
    }, [defaultValueType]);

    const transform = useCallback((constants: SortableItem<KeyValueItem>[]): DictType<string | object> => {
        const transformedConstants = constants.map(item => {
            let transformedValue: string | object;
            if (defaultValueType[item.id] === "object") {
                try {
                    transformedValue = JSON.parse(item.data.value);
                } catch (e) {
                    transformedValue = item.data.value;
                }
            } else if (defaultValueType[item.id] === "object-string") {
                transformedValue = formatJSON(item.data.value);
            } else {
                transformedValue = item.data.value;
            }

            return {
                key: item.data.key.trim(),
                value: transformedValue,
            };
        });
        return keyValueArrayToDict<string | object>(transformedConstants);
    }, [defaultValueType]);

    const clear = useCallback(() => {
        isFirstChange.current = true;
        setDefaultValueType({});
    }, []);

    useEffect(() => {
        if (!isFirstChange.current) {
            return;
        }
        isFirstChange.current = false;
        const defaultValueType: DictType<ValueType> = (
            Object.fromEntries(sortableConstants.map(constant => [constant.id, valueFormatType(constant.data.value)]))
        );
        setDefaultValueType(defaultValueType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortableConstants]);

    return useMemo(() => ({
        getFormatChangedKeys,
        transform,
        clear
    }), [getFormatChangedKeys, transform, clear]);
};
